import React, { useEffect, useState } from 'react';
import {
  configApi,
  BusinessTierLimit,
  UpdateBusinessTierLimitPayload,
} from '../../../api/config';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useAuth } from '../../../hooks/useAuth';
import { useToast } from '../../../hooks/useToast';

/**
 * The business ladder, edited.
 *
 * Separate from the consumer one on both sides — a separate table, and this
 * separate section — because the numbers answer a different regulatory
 * question and one screen editing both is how a change to consumer limits
 * silently reprices every business account.
 *
 * ## Blank is uncapped, and 0 is not
 *
 * `null` means no ceiling; `0` means "may not transfer at all". They are
 * different statements, and conflating them is how an uncapped tier ends up
 * blocking every payment on it. An empty field here sends null, and the
 * table renders it as "Uncapped" rather than as a dash that could be either.
 *
 * ## These are ceilings, not settings
 *
 * A business can hold itself to less from its own Transfer limits screen.
 * What is set here is the most it may ever choose — so lowering a tier can
 * silently tighten what businesses already had, which is why saving asks
 * first.
 */
const formatNaira = (n: number | null | undefined) =>
  n === null || n === undefined
    ? 'Uncapped'
    : `₦${Number(n).toLocaleString('en-NG')}`;

/** Empty means uncapped. Anything unparseable is refused rather than
 *  silently becoming zero, which would be the harshest possible reading. */
const parseLimit = (value: string): number | null | undefined => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

interface EditState {
  singleTransferLimit: string;
  dailyLimit: string;
  cardLimit: string;
  internationalEnabled: boolean;
}

const toEditState = (limit: BusinessTierLimit): EditState => ({
  singleTransferLimit: limit.singleTransferLimit?.toString() ?? '',
  dailyLimit: limit.dailyLimit?.toString() ?? '',
  cardLimit: limit.cardLimit?.toString() ?? '',
  internationalEnabled: limit.internationalEnabled,
});

/** Only the figures the backend actually enforces. "Monthly" and "Balance"
 *  used to sit here; they were editable, they saved, and nothing on the
 *  backend ever compared anything against them, so this screen was offering
 *  a control that did nothing. The columns are gone from the API — sending
 *  them now is a 400, since request bodies are validated strictly. */
const FIELDS: { key: keyof EditState; label: string }[] = [
  { key: 'singleTransferLimit', label: 'Per transfer' },
  { key: 'dailyLimit', label: 'Daily' },
  { key: 'cardLimit', label: 'Card' },
];

const inputStyle: React.CSSProperties = {
  width: 120,
  height: 34,
  padding: '0 8px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-800)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--blue-600)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
};

const cellStyle: React.CSSProperties = {
  padding: '12px 20px',
  borderBottom: '1px solid var(--neutral-200)',
};

export const BusinessTierLimits: React.FC = () => {
  const { admin } = useAuth();
  const toast = useToast();
  const [limits, setLimits] = useState<BusinessTierLimit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTier, setEditingTier] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [pending, setPending] = useState<
    (UpdateBusinessTierLimitPayload & { tier: number; name: string }) | null
  >(null);
  const [saving, setSaving] = useState(false);

  const canEdit =
    admin && (admin.role === 'super_admin' || admin.role === 'finance');

  const fetchLimits = async () => {
    try {
      setLoading(true);
      setError(null);
      setLimits(await configApi.getBusinessTierLimits());
    } catch {
      setError('Failed to load business tier limits.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchLimits();
  }, []);

  const handleSave = (limit: BusinessTierLimit) => {
    if (!editState) return;

    /* Keyed off FIELDS rather than by position. The payload used to read
       `parsed[0]`…`parsed[4]` against the array order, so removing a field
       from FIELDS silently shifted every figure after it into the wrong
       column — a per-transfer ceiling saved as a card limit. */
    const parsed: Partial<Record<keyof EditState, number | null>> = {};
    for (const field of FIELDS) {
      const value = parseLimit(editState[field.key] as string);
      if (value === undefined) {
        toast.error('Enter a number, or leave a field empty for no limit.');
        return;
      }
      parsed[field.key] = value;
    }

    setPending({
      tier: limit.tier,
      name: limit.name,
      singleTransferLimit: parsed.singleTransferLimit,
      dailyLimit: parsed.dailyLimit,
      cardLimit: parsed.cardLimit,
      internationalEnabled: editState.internationalEnabled,
    });
    setEditingTier(null);
  };

  const executeUpdate = async () => {
    if (!pending) return;
    try {
      setSaving(true);
      const { tier, name, ...updates } = pending;
      await configApi.updateBusinessTierLimit(tier, updates);
      await fetchLimits();
      setPending(null);
      toast.success(`${name} limits updated.`);
    } catch (err) {
      toast.error(err, 'Failed to update business tier limit.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <>
      <Card padding={0}>
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--neutral-300)',
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
            Business Tier Limits
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
            The ceiling for each KYB tier. Businesses can hold themselves to less from
            their own settings — this is the most they may ever choose. Leave a field
            empty for no limit.
          </p>
        </div>

        {error && (
          <div
            style={{
              margin: 20,
              padding: 12,
              background: 'var(--danger-100)',
              color: 'var(--danger-900)',
              fontSize: 13,
              borderRadius: 'var(--radius-md)',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
          >
            <thead>
              <tr style={{ background: 'var(--neutral-100)' }}>
                <th style={{ ...cellStyle, textAlign: 'left' }}>Tier</th>
                {FIELDS.map((f) => (
                  <th key={f.key} style={{ ...cellStyle, textAlign: 'left' }}>
                    {f.label}
                  </th>
                ))}
                <th style={{ ...cellStyle, textAlign: 'left' }}>International</th>
                <th style={{ ...cellStyle, textAlign: 'right' }} />
              </tr>
            </thead>
            <tbody>
              {limits.map((limit) => {
                const editing = editingTier === limit.tier && editState;
                return (
                  <tr key={limit.id}>
                    <td style={cellStyle}>
                      <div style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>
                        Tier {limit.tier}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                        {limit.name}
                      </div>
                    </td>

                    {FIELDS.map((f) => (
                      <td key={f.key} style={cellStyle}>
                        {editing ? (
                          <input
                            type="number"
                            placeholder="No limit"
                            value={editState![f.key] as string}
                            onChange={(e) =>
                              setEditState({
                                ...editState!,
                                [f.key]: e.target.value,
                              })
                            }
                            style={inputStyle}
                          />
                        ) : (
                          formatNaira(
                            limit[f.key as keyof BusinessTierLimit] as
                              | number
                              | null,
                          )
                        )}
                      </td>
                    ))}

                    <td style={cellStyle}>
                      {editing ? (
                        <input
                          type="checkbox"
                          checked={editState!.internationalEnabled}
                          onChange={(e) =>
                            setEditState({
                              ...editState!,
                              internationalEnabled: e.target.checked,
                            })
                          }
                        />
                      ) : limit.internationalEnabled ? (
                        'Enabled'
                      ) : (
                        'Off'
                      )}
                    </td>

                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      {!canEdit ? null : editing ? (
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setEditingTier(null);
                              setEditState(null);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button size="sm" onClick={() => handleSave(limit)}>
                            Save
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setEditingTier(limit.tier);
                            setEditState(toEditState(limit));
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmDialog
        isOpen={pending !== null}
        title={`Change ${pending?.name ?? ''} limits?`}
        /* Lowering a ceiling can tighten what businesses already had set for
           themselves, and it applies to every business on the tier at once. */
        message="This applies to every business on this tier immediately. Lowering a ceiling also caps any business that had chosen a higher figure for itself."
        confirmLabel={saving ? 'Saving…' : 'Apply'}
        onConfirm={() => void executeUpdate()}
        onCancel={() => setPending(null)}
      />
    </>
  );
};
